import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Button, Dropdown, Spin, Space, Avatar, Drawer, Breadcrumb, message } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  CarOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  DollarOutlined,
  LogoutOutlined,
  UserOutlined,
  CarryOutOutlined,
  SolutionOutlined,
  CompassOutlined,
  RobotOutlined,
  MenuOutlined,
  LeftOutlined,
  RightOutlined,
  ShopOutlined,
  PictureOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './auth.jsx';
import { THEMES, getThemeKeys, switchTheme } from './theme.js';
import Login from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Orders from './pages/Orders.jsx';
import Logistics from './pages/Logistics.jsx';
import Products from './pages/Products.jsx';
import RegionAnalysis from './pages/RegionAnalysis.jsx';
import FinanceAnalysis from './pages/FinanceAnalysis.jsx';
import FreightTemplate from './pages/FreightTemplate.jsx';
import AfterSales from './pages/AfterSales.jsx';
import Inventory from './pages/Inventory.jsx';
import InventoryLogs from './pages/InventoryLogs.jsx';
import Purchase from './pages/Purchase.jsx';
import DailyInbound from './pages/DailyInbound.jsx';
import Users from './pages/Users.jsx';
import AiAssistant from './pages/AiAssistant.jsx';
import TinaAssistant from './pages/TinaAssistant.jsx';
import HeboAssistant from './pages/HeboAssistant.jsx';
import LiAssistant from './pages/LiAssistant.jsx';
import TeamBoard from './pages/TeamBoard.jsx';
import MindMap from './pages/MindMap.jsx';
import OperationDiary from './pages/OperationDiary.jsx';
import CommandPalette from './components/CommandPalette.jsx';
import CompetitorMonitor from './pages/CompetitorMonitor.jsx';
import StoreData from './pages/StoreData.jsx';
import MpConfig from './pages/MpConfig.jsx';
import ImageWorkbench from './pages/ImageWorkbench.jsx';
import ProfitCalc from './pages/ProfitCalc.jsx';
import BlastPlan from './pages/BlastPlan.jsx';
import ReconcileCenter from './pages/ReconcileCenter.jsx';
import Crm from './pages/Crm.jsx';
import Receivables from './pages/Receivables.jsx';
import DecisionBoard from './pages/DecisionBoard.jsx';
import LinkAttribution from './pages/LinkAttribution.jsx';
import ShopAttribution from './pages/ShopAttribution.jsx';
import LedgerFlow from './pages/LedgerFlow.jsx';
import FinanceHealth from './pages/FinanceHealth.jsx';
import CashAccount from './pages/CashAccount.jsx';
import SocialInsurance from './pages/SocialInsurance.jsx';
import TaxCenter from './pages/TaxCenter.jsx';

const { Header, Sider, Content } = Layout;

// 旧地址 → 新家（2026-09-24 账单核算/对账中心收敛后的迁移映射）
const LEGACY_REDIRECTS = {
  '/bill-reconcile': { to: '/ledger-flow?tab=match', tip: '「账单核算」已迁移到：财务管理 → 账务流水 → 对账核对 页签' },
  '/freight-bill': { to: '/reconcile?tab=freight', tip: '「快递对账」已迁移到：财务管理 → 对账中心 → 快递对账 页签' },
  '/order-verify': { to: '/reconcile?tab=order', tip: '「订单校验」已迁移到：财务管理 → 对账中心 → 订单校验 页签' },
};

// 全局错误边界：任何页面组件 JS 报错时显示友好提示+刷新按钮，避免空白页（便于排查）
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] 页面渲染错误:', error, info);
  }
  render() {
    if (this.state.error) {
      const msg = (this.state.error?.message || String(this.state.error || '')).slice(0, 600);
      return (
        <div style={{ padding: 32, color: '#CF1322' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>页面加载出错</div>
          <div style={{ color: '#434343', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4, padding: 12, marginBottom: 16, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{msg}</div>
          <Button type="primary" onClick={() => { window.location.reload(); }}>刷新页面</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const menuItems = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '首页' },
  {
    key: 'group-sales', icon: <ShoppingOutlined />, label: '销售运营',
    children: [
      { key: '/orders', label: '订单管理' },
      { key: '/crm', label: '客户管理' },
      { key: '/receivables', label: '客户回款' },
      { key: '/logistics', label: '物流追踪' },
      { key: '/aftersales', label: '售后管理' },
    ],
  },
  {
    key: 'group-supply', icon: <CarryOutOutlined />, label: '供应链',
    children: [
      { key: '/inventory', label: '库存管理' },
      { key: '/inventory-logs', label: '库存变动记录' },
      { key: '/purchase', label: '采购管理' },
      { key: '/daily-inbound', label: '每日入库' },
      { key: '/products', label: '商品成本' },
      { key: '/freight', label: '运费模板' },
    ],
  },
  {
    key: 'group-fin', icon: <BarChartOutlined />, label: '经营分析',
    children: [
      { key: '/profit-calc', label: '盈亏计算' },
      { key: '/blast-plan', label: '项目预测' },
      { key: '/region', label: '区域分析' },
      { key: '/link-attribution', label: '推广归因' },
      { key: '/shop-attribution', label: '店铺归因' },
    ],
  },
  {
    key: 'group-op', icon: <CompassOutlined />, label: '运营管理',
    children: [
      { key: '/mindmap', label: '思维导图' },
      { key: '/diary', label: '运营日记' },
      { key: '/competitor', label: '竞品监控' },
      { key: '/store-data', label: '店铺数据' },
      { key: '/ecommerce-image', label: 'AI云图' },
    ],
  },
  {
    key: 'group-finance', icon: <DollarOutlined />, label: '财务管理',
    children: [
      { type: 'group', label: '经营看板', children: [
        { key: '/decision-board', label: '公司看板' },
        { key: '/finance-health', label: '财务健康' },
        { key: '/finance', label: '财务分析' },
      ] },
      { type: 'group', label: '资金核算', children: [
        { key: '/cash-account', label: '资金中心' },
        { key: '/ledger-flow', label: '账务流水' },
        { key: '/reconcile', label: '对账中心' },
      ] },
      { type: 'group', label: '财税合规', children: [
        { key: '/social', label: '员工社保' },
        { key: '/tax-center', label: '税务中心' },
      ] },
    ],
  },
  {
    key: 'group-sys', icon: <SolutionOutlined />, label: '系统管理',
    children: [
      { key: '/users', label: '用户与权限' },
      { key: '/mp-config', label: '移动端配置' },
    ],
  },
  {
    key: 'group-ai', icon: <RobotOutlined />, label: 'AI 员工',
    children: [
      { key: '/ai', label: 'AI 助手' },
      { key: '/tina', label: '缇娜助手' },
      { key: '/hebo', label: '河伯助手' },
      { key: '/xiaoli', label: '小李助手' },
      { key: '/team', label: 'AI 团队' },
    ],
  },
];

// 递归收集所有叶子菜单 key（兼容 type:'group' 分组嵌套）
function collectLeafKeys(items) {
  const out = [];
  (items || []).forEach(m => {
    if (!m) return;
    if (m.children) out.push(...collectLeafKeys(m.children));
    else if (m.key) out.push(m.key);
  });
  return out;
}
// 所有叶子菜单 key（含分组内）
const allLeafKeys = collectLeafKeys(menuItems);

// 按角色过滤菜单：admin 全部 / warehouse 仅供应链（库存/采购）/ other 仅首页 / guest 仅盈亏计算
function filterMenuByRole(items, role) {
  if (role === 'admin') return items;
  const visible = role === 'warehouse' ? ['/inventory', '/inventory-logs', '/purchase', '/daily-inbound', '/aftersales', '/products']
    : role === 'guest' ? ['/profit-calc']
    : ['/dashboard'];
  return items.map(m => {
    if (m.children) {
      const c = m.children.filter(x => visible.includes(x.key));
      return c.length ? { ...m, children: c } : null;
    }
    return visible.includes(m.key) ? m : null;
  }).filter(Boolean);
}
// 每个角色默认落地页
const roleHome = { admin: '/dashboard', warehouse: '/inventory', other: '/dashboard', guest: '/profit-calc' };

// 品牌 Logo：珊瑚橙圆角方块 + 店铺图标
function BrandLogo({ collapsed }) {
  return (
    <div style={{
      height: 60, margin: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 10, whiteSpace: 'nowrap', overflow: 'hidden',
    }}>
      <div style={{
        flex: '0 0 auto', width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,var(--grad-from),var(--brand))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 3px 8px rgba(var(--brand-rgb),0.35)',
      }}>
        <ShopOutlined style={{ fontSize: 18, color: '#fff' }} />
      </div>
      {!collapsed && (
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--brand-strong)', letterSpacing: 0.5 }}>
          电商管理工作台
        </span>
      )}
    </div>
  );
}

// 根据路由 key 找到面包屑路径 [分组名, 页面名]（首页无分组，兼容 group 分组嵌套）
function findBreadcrumb(key) {
  for (const m of menuItems) {
    if (m.key === key && !m.children) return [null, m.label];
    if (m.children) {
      for (const c of m.children) {
        if (c.type === 'group') {
          const g = (c.children || []).find(x => x.key === key);
          if (g) return [m.label, g.label];
        } else if (c.key === key) {
          return [m.label, c.label];
        }
      }
    }
  }
  return [null, '首页'];
}

export default function App({ themeKey = 'candy' }) {
  const { user, logout, loading } = useAuth();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
  });
  const [animating, setAnimating] = useState(false);
  const animTimer = useRef(null);
  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebarCollapsed', next ? '1' : '0'); } catch {}
      return next;
    });
    // 动画期间临时关掉全站毛玻璃（backdrop-filter 每帧重算模糊是卡顿主因），结束后恢复
    setAnimating(true);
    if (animTimer.current) clearTimeout(animTimer.current);
    animTimer.current = setTimeout(() => setAnimating(false), 300);
  };
  const navigate = useNavigate();
  const location = useLocation();
  // 旧「账单核算 / 快递对账 / 订单校验」地址：迁移提示 + 跳新家（2026-09-26 ACCIO 审查项4）
  // ⚠️ useEffect 必须在 loading/!user 提前 return 之前（Hooks 规则），迁移表放模块外无依赖
  const legacyPath = '/' + (location.pathname || '').split('/')[1];
  useEffect(() => {
    const hit = LEGACY_REDIRECTS[legacyPath];
    if (hit) {
      message.info({ content: hit.tip, duration: 6 });
      navigate(hit.to, { replace: true });
    }
  }, [legacyPath, navigate]);
  // 手机端检测：<768px 时侧边栏改为抽屉
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const h = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  // 权限路由保护：无权限页面自动跳转到角色首页
  useEffect(() => {
    if (loading || !user) return;
    const r = user.role || 'other';
    const vm = filterMenuByRole(menuItems, r);
    const keys = collectLeafKeys(vm);
    const p = '/' + location.pathname.split('/')[1];
    // 旧地址由 LEGACY_REDIRECTS 接管跳转（否则同轮 effect 竞态会抢先弹去 dashboard）
    if (LEGACY_REDIRECTS[p]) return;
    if (!keys.includes(p)) navigate(roleHome[r] || '/dashboard');
  }, [loading, user, location.pathname, navigate]);

  // 兜底：清除弹框/抽屉残留的页面滚动锁（antd Modal 偶发 body overflow:hidden 未恢复，导致页面滚不动）
  useEffect(() => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.documentElement.style.overflow = '';
  }, [location.pathname]);

  // 点击涟漪：KPI 统计卡（stat-card / kpi-tap）点击时，从点击处扩散一圈光波（不裁剪，不影响下拉/气泡浮层）
  useEffect(() => {
    const handler = (e) => {
      const host = e.target.closest('.stat-card, .kpi-tap');
      if (!host) return;
      const cs = getComputedStyle(host);
      if (cs.position === 'static') host.style.position = 'relative';
      const rect = host.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2;
      const r = document.createElement('span');
      r.className = 'ui-ripple';
      r.style.width = r.style.height = size + 'px';
      r.style.left = (e.clientX - rect.left - size / 2) + 'px';
      r.style.top = (e.clientY - rect.top - size / 2) + 'px';
      host.appendChild(r);
      setTimeout(() => r.remove(), 650);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // 加载中
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--brand-bg)' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  // 未登录
  if (!user) {
    return <Login />;
  }

  const currentPath = '/' + location.pathname.split('/')[1];
  const role = (user && user.role) || 'other';
  const isGuest = role === 'guest';
  // 按角色过滤菜单与可访问页面
  const visibleMenu = filterMenuByRole(menuItems, role);
  const menuLeafKeys = collectLeafKeys(visibleMenu);
  // 路由保护：无权限页面重定向到角色首页
  const allowedPath = menuLeafKeys.includes(currentPath) ? currentPath : (roleHome[role] || '/dashboard');
  const selectedKey = allowedPath;
  const [groupLabel, pageLabel] = findBreadcrumb(selectedKey);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const userMenu = {
    items: [
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        onClick: handleLogout,
      },
    ],
  };

  // 主题切换菜单：5 套主题，当前主题打勾高亮
  const themeMenu = {
    items: getThemeKeys().map((key) => {
      const t = THEMES[key];
      const active = key === themeKey;
      return {
        key,
        label: (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: t.brand, display: 'inline-block', boxShadow: '0 0 0 2px #fff, 0 0 0 3px rgba(0,0,0,0.08)' }} />
            <span style={{ fontWeight: active ? 600 : 400, color: active ? 'var(--brand-strong)' : '#26201C' }}>{t.label}</span>
            {active && <span style={{ marginLeft: 'auto', color: 'var(--brand)', fontSize: 12 }}>●</span>}
          </span>
        ),
      };
    }),
    onClick: ({ key }) => { if (key !== themeKey) switchTheme(key); },
  };

  const siderMenu = (
    <Menu theme="light" mode="inline" selectedKeys={[selectedKey]}
      defaultOpenKeys={['group-sales']}
      items={visibleMenu}
      onClick={({ key }) => navigate(key)} />
  );

  return (
    <>
      {/* 背景光影动效层：光斑（主题色+淡蓝点缀）缓慢漂移/呼吸 + 流光粒子 */}
      <div className="aurora" aria-hidden="true">
        <div className="mist"></div>
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
        <div className="blob blob-4"></div>
        <div className="blob blob-5"></div>
        <div className="spark spark-1"></div>
        <div className="spark spark-2"></div>
        <div className="spark spark-3"></div>
        <div className="spark spark-4"></div>
        <div className="spark spark-5"></div>
        <div className="spark spark-6"></div>
        <div className="spark spark-7"></div>
        <div className="spark spark-8"></div>
        <div className="spark spark-9"></div>
        <div className="spark spark-10"></div>
        <div className="spark spark-11"></div>
        <div className="spark spark-12"></div>
        <div className="spark spark-13"></div>
        <div className="spark spark-14"></div>
        <div className="spark spark-15"></div>
        <div className="spark spark-16"></div>
      </div>
      <Layout className={animating ? 'sidebar-animating' : undefined} style={{ minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {!isMobile && (
        <Sider trigger={null} collapsed={collapsed} width={216} collapsedWidth={64} className="app-sider-float">
          <div className="app-sider-body">
            <BrandLogo collapsed={collapsed} />
            <div className="app-sider-menu">{siderMenu}</div>
            <div className="app-sider-footer">
              <Button type="text" className="app-sider-toggle" onClick={toggleCollapse}
                title={collapsed ? '展开侧边栏' : '收起侧边栏'}>
                {collapsed ? <RightOutlined /> : <LeftOutlined />}
                {!collapsed && <span className="app-sider-toggle-text">收起</span>}
              </Button>
            </div>
          </div>
        </Sider>
      )}
      <Layout>
        <Header className={isMobile ? undefined : 'app-header-float'} style={{
          background: 'transparent', padding: isMobile ? '0 12px' : '0 20px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          height: isMobile ? 52 : 46, lineHeight: isMobile ? '52px' : '46px',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {isMobile && (
              <Button type="text" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} style={{ marginRight: -4 }} />
            )}
            {isMobile ? (
              <span style={{ fontSize: 16, fontWeight: 600, color: '#1f1f1f' }}>
                {pageLabel || '首页'}
              </span>
            ) : (
              <Breadcrumb
                style={{ fontSize: 14, lineHeight: '46px' }}
                items={[
                  { title: <span style={{ color: '#A99C90' }}>电商工作台</span> },
                  ...(groupLabel ? [{ title: <span style={{ color: '#8C8178' }}>{groupLabel}</span> }] : []),
                  { title: <span style={{ color: '#26201C', fontWeight: 500 }}>{pageLabel || '首页'}</span> },
                ]}
              />
            )}
          </span>
          <Space size="middle">
            <Dropdown menu={themeMenu} placement="bottomRight" trigger={['click']}>
              <Button type="text" size="middle"
                icon={<BgColorsOutlined style={{ fontSize: 18, color: 'var(--brand)' }} />}
                style={{ padding: '0 8px', height: 40 }}
                title="切换主题" />
            </Dropdown>
            <Dropdown menu={userMenu} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar size={30} icon={<UserOutlined />} style={{ background: 'var(--brand)' }} />
                {!isMobile && <span style={{ fontSize: 14, color: '#26201C', fontWeight: 500 }}>{user.name}</span>}
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ margin: isMobile ? 0 : '0 16px 16px 0', minHeight: 280 }}>
          <ErrorBoundary>
            {/* 页面常驻：切换路由只隐藏/显示，不卸载 → 原页面状态（筛选/分页/搜索/滚动）保留，切回不重新加载 */}
            {/* guest 角色下只渲染盈亏计算（其他页面挂载时会发 API 被 403，返回错误对象会触发 .map(undefined) 崩溃） */}
            {(!isGuest && (allowedPath === '/' || allowedPath === '/dashboard')) && <Dashboard />}
            {(!isGuest && allowedPath === '/orders') && <Orders />}
            {(!isGuest && allowedPath === '/crm') && <Crm />}
            {(!isGuest && allowedPath === '/receivables') && <Receivables />}
            {(!isGuest && allowedPath === '/logistics') && <Logistics />}
            {(!isGuest && allowedPath === '/aftersales') && <AfterSales />}
            {(!isGuest && allowedPath === '/inventory') && <Inventory />}
            {(!isGuest && allowedPath === '/inventory-logs') && <InventoryLogs />}
            {(!isGuest && allowedPath === '/daily-inbound') && <DailyInbound />}
            {(!isGuest && allowedPath === '/purchase') && <Purchase />}
            {(!isGuest && allowedPath === '/products') && <Products />}
            {(!isGuest && allowedPath === '/freight') && <FreightTemplate />}
            {(!isGuest && allowedPath === '/region') && <RegionAnalysis />}
            {(!isGuest && allowedPath === '/finance') && <FinanceAnalysis />}
            {(!isGuest && allowedPath === '/decision-board') && <DecisionBoard />}
            {(!isGuest && allowedPath === '/finance-health') && <FinanceHealth />}
            {(!isGuest && allowedPath === '/reconcile') && <ReconcileCenter />}
            {(!isGuest && allowedPath === '/users') && <Users />}
            {(!isGuest && allowedPath === '/mp-config') && <MpConfig />}
            {(!isGuest && allowedPath === '/ai') && <AiAssistant />}
            {(!isGuest && allowedPath === '/tina') && <TinaAssistant />}
            {(!isGuest && allowedPath === '/hebo') && <HeboAssistant />}
            {(!isGuest && allowedPath === '/xiaoli') && <LiAssistant />}
            {(!isGuest && allowedPath === '/team') && <TeamBoard />}
            {(!isGuest && allowedPath === '/mindmap') && <MindMap />}
            {(!isGuest && allowedPath === '/diary') && <OperationDiary />}
            {(!isGuest && allowedPath === '/competitor') && <CompetitorMonitor />}
            {(!isGuest && allowedPath === '/store-data') && <StoreData />}
            {(!isGuest && allowedPath === '/ecommerce-image') && <ImageWorkbench />}
            {(allowedPath === '/profit-calc') && <ProfitCalc />}
            {(!isGuest && allowedPath === '/blast-plan') && <BlastPlan />}
            {(!isGuest && allowedPath === '/link-attribution') && <LinkAttribution />}
            {(!isGuest && allowedPath === '/shop-attribution') && <ShopAttribution />}
            {(!isGuest && allowedPath === '/ledger-flow') && <LedgerFlow />}
            {(!isGuest && allowedPath === '/cash-account') && <CashAccount />}
            {(!isGuest && allowedPath === '/social') && <SocialInsurance />}
            {(!isGuest && allowedPath === '/tax-center') && <TaxCenter />}
          </ErrorBoundary>
        </Content>
      </Layout>
      {/* 手机端抽屉菜单 */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          width={232}
          styles={{ body: { padding: 0, background: '#fff' } }}
        >
          <div style={{ height: 60, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--brand-border)' }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg,var(--grad-from),var(--brand))', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 8px rgba(var(--brand-rgb),0.35)' }}>
              <ShopOutlined style={{ fontSize: 17, color: '#fff' }} />
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--brand-strong)' }}>电商管理工作台</span>
          </div>
          <Menu theme="light" mode="inline" selectedKeys={[selectedKey]}
            defaultOpenKeys={['group-sales']}
            items={visibleMenu}
            onClick={({ key }) => { navigate(key); setDrawerOpen(false); }} />
        </Drawer>
      )}
      {role !== 'guest' && <CommandPalette onNavigate={navigate} />}
    </Layout>
    </>
  );
}
